import React, { useState, useEffect } from 'react';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress,
} from '@mui/material';
import axiosInstance from '../../lib/axios';
import { toast } from 'react-toastify';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CalendarReport = ({
    selectedProperty,
    selectedShift,
    selectedYojnas,
    fromDate,
    toDate,
}) => {
    const [rows, setRows] = useState([]);
    const [workAreas, setWorkAreas] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchCalendarTasks = async () => {
        if (!selectedProperty || !selectedShift || !fromDate) {
            setRows([]);
            setWorkAreas([]);
            return;
        }

        setLoading(true);
        try {
            const payload = {
                PropId: parseInt(selectedProperty),
                YojanaId: 1,
                ShiftId: parseInt(selectedShift),
                HkWrkAreaId: 1,
                StrHkTaskId: "",
                StrYojanaId: selectedYojnas.length > 0 ? selectedYojnas.join(',') : "",
                StrHkWrkAreaId: "",
                WrkRecvDt: fromDate,
                WrkRecvToDt: toDate || fromDate,
                Flag: "DateWiseRptDtDetails",
            };

            const response = await axiosInstance.post(
                'MstYojana/RetriveGetTrnWrkRecvTaskDtWise',
                payload
            );

            if (response.data.Status === 0 && response.data.Data?.length > 0) {
                const data = response.data.Data;

                // Unique work areas
                const waSet = new Set(data.map(item => item.HkWrkAreaName?.trim()).filter(Boolean));
                const sortedWorkAreas = [...waSet].sort();

                // Group by task + date
                const grouped = new Map();

                data.forEach(item => {
                    const task = item.HkTaskName?.trim() || 'Unknown Task';
                    const dateKey = item.WrkRecvDt || 'Unknown Date';
                    const key = `${task}|||${dateKey}`;

                    if (!grouped.has(key)) {
                        grouped.set(key, {
                            task,
                            date: dateKey,
                            workAreaValues: Object.fromEntries(sortedWorkAreas.map(wa => [wa, false])),
                        });
                    }

                    const entry = grouped.get(key);
                    const wa = item.HkWrkAreaName?.trim();

                    if (wa && entry.workAreaValues[wa] !== undefined) {
                        const dateObj = new Date(dateKey);
                        const dayIndex = dateObj.getDay();
                        const dayKey = DAYS[dayIndex];
                        const actualFlag = item[`${dayKey}A`];

                        if (actualFlag === "1") {
                            entry.workAreaValues[wa] = true;
                        }
                    }
                });

                const sortedRows = [...grouped.values()].sort((a, b) => {
                    if (a.date !== b.date) return a.date.localeCompare(b.date);
                    return a.task.localeCompare(b.task);
                });

                setRows(sortedRows);
                setWorkAreas(sortedWorkAreas);
            } else {
                setRows([]);
                setWorkAreas([]);
            }
        } catch (err) {
            toast.error("Failed to load calendar data");
            setRows([]);
            setWorkAreas([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCalendarTasks();
    }, [selectedProperty, selectedShift, selectedYojnas, fromDate, toDate]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            });
        } catch {
            return dateStr;
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>
                Task Completion Calendar
                {fromDate && toDate && (
                    <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 2 }}>
                        ({formatDate(fromDate)} – {formatDate(toDate)})
                    </Typography>
                )}
            </Typography>

            {loading ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                    <CircularProgress size="3rem" />
                    <Typography sx={{ mt: 1, color: 'green' }}>Loading Data</Typography>
                </Box>
            ) : (
                <>
                    <TableContainer
                        component={Paper}
                        sx={{
                            flex: 1,
                            maxHeight: '60vh',
                            overflow: 'auto',
                            border: '1px solid #e0e0e0',
                        }}
                    >
                        <Table stickyHeader size="small" sx={{ minWidth: 'max-content' }}>
                            <TableHead>
                                <TableRow>
                                    {/* Task column commented out as requested */}
                                    {/* <TableCell
                                        sx={{
                                            bgcolor: '#635bff',
                                            color: 'white',
                                            fontWeight: 600,
                                            minWidth: 240,
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 3,
                                            boxShadow: '2px 0 6px -2px rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        Task
                                    </TableCell> */}

                                    <TableCell
                                        align="center"
                                        sx={{
                                            bgcolor: '#635bff',
                                            color: 'white',
                                            fontWeight: 600,
                                            minWidth: 160,
                                            position: 'sticky',
                                            left: 0,              //  left: 240,  now left: 0 since it's the first column
                                            zIndex: 3,
                                            boxShadow: '2px 0 6px -2px rgba(0,0,0,0.1)',
                                        }}
                                    >
                                        Date
                                    </TableCell>

                                    {workAreas.map((wa) => (
                                        <TableCell
                                            key={wa}
                                            align="center"
                                            sx={{
                                                bgcolor: '#1976d2',
                                                color: 'white',
                                                fontWeight: 600,
                                                minWidth: 90,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {wa}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={1 + workAreas.length} align="center" sx={{ py: 8 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                No data found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row, idx) => (
                                        <TableRow key={idx} hover>
                                            {/* Task body commented out */}
                                            {/* <TableCell
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    backgroundColor: 'white',
                                                    zIndex: 1,
                                                    fontWeight: 500,
                                                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.08)',
                                                }}
                                            >
                                                {row.task}
                                            </TableCell> */}

                                            <TableCell
                                                align="center"
                                                sx={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    backgroundColor: 'white',
                                                    zIndex: 1,
                                                    boxShadow: '2px 0 4px -2px rgba(0,0,0,0.08)',
                                                }}
                                            >
                                                {formatDate(row.date)}
                                            </TableCell>

                                            {workAreas.map((wa) => (
                                                <TableCell
                                                    key={wa}
                                                    align="center"
                                                    sx={{
                                                        backgroundColor: row.workAreaValues[wa] ? '#e8f5e9' : '#f5f3f3',
                                                        color: row.workAreaValues[wa] ? '#2e7d32' : '#ca2121',
                                                        fontWeight: row.workAreaValues[wa] ? 600 : 400,
                                                    }}
                                                >
                                                    {row.workAreaValues[wa] ? 'Yes' : 'No'}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {rows.length > 0 && (
                        <Box sx={{ mt: 1.5, textAlign: 'right', color: 'text.secondary', fontSize: '0.875rem' }}>
                            Showing {rows.length} record{rows.length !== 1 ? 's' : ''} • {workAreas.length} work areas
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default CalendarReport;